import { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import {
    LayoutDashboard, Users, CalendarDays, CreditCard, Settings, LogOut,
    Dumbbell, ListChecks, Rocket, MessageSquare, Utensils, Timer,
    FileText, Menu, X, ChevronDown, Send, Inbox,
} from "lucide-react";

const PROGRAMMING_ITEMS = [
    { to: "/programs", label: "Programs", icon: ListChecks, testid: "nav-programs" },
    { to: "/exercises", label: "Exercises", icon: Dumbbell, testid: "nav-exercises" },
    { to: "/meal-plans", label: "Meal Plans", icon: Utensils, testid: "nav-meal-plans" },
    { to: "/nutrition-sent", label: "Nutrition Sent", icon: Send, testid: "nav-nutrition-sent" },
    { to: "/calendar", label: "Calendar", icon: CalendarDays, testid: "nav-calendar" },
];

const NAV = [
    { to: "/", label: "Overview", icon: LayoutDashboard, end: true, testid: "nav-overview" },
    { to: "/leads", label: "Leads", icon: Inbox, testid: "nav-leads" },
    { to: "/clients", label: "Clients", icon: Users, testid: "nav-clients" },
    { to: "/messages", label: "Messages", icon: MessageSquare, testid: "nav-messages", badgeKey: "msgs" },
    { to: "/documents", label: "Documents", icon: FileText, testid: "nav-documents", badgeKey: "docs" },
    { kind: "group", label: "Programming", icon: ListChecks, testid: "nav-programming", items: PROGRAMMING_ITEMS },
    { to: "/timer", label: "Timer", icon: Timer, testid: "nav-timer" },
    { to: "/growth", label: "Growth", icon: Rocket, testid: "nav-growth" },
    { to: "/payments", label: "Payments", icon: CreditCard, testid: "nav-payments" },
    { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
];

export default function Layout({ children }) {
    const { user, logout } = useAuth();
    const nav = useNavigate();
    const location = useLocation();
    const [badges, setBadges] = useState({ docs: 0, msgs: 0 });
    const programmingPaths = PROGRAMMING_ITEMS.map((i) => i.to);
    const [progOpen, setProgOpen] = useState(() => programmingPaths.some((p) => location.pathname.startsWith(p)));
    useEffect(() => {
        if (programmingPaths.some((p) => location.pathname.startsWith(p))) setProgOpen(true);
        // eslint-disable-next-line
    }, [location.pathname]);

    useEffect(() => {
        const loadBadges = async () => {
            try {
                const [d, m] = await Promise.all([
                    api.get("/documents/assignments/pending-count"),
                    api.get("/messages/unread-count"),
                ]);
                setBadges({ docs: d.data.count || 0, msgs: m.data.count || 0 });
            } catch (_e) {}
        };
        loadBadges();
        const t = setInterval(loadBadges, 15000);
        return () => clearInterval(t);
    }, []);

    const [open, setOpen] = useState(() => {
        if (typeof window === "undefined") return true;
        const saved = localStorage.getItem("nde_sidebar_open");
        if (saved !== null) return saved === "1";
        return window.innerWidth >= 1024;
    });

    useEffect(() => {
        localStorage.setItem("nde_sidebar_open", open ? "1" : "0");
    }, [open]);

    const closeOnMobile = () => {
        if (typeof window !== "undefined" && window.innerWidth < 1024) setOpen(false);
    };

    return (
        <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] relative" data-testid="app-shell">
            {/* Open (hamburger) button — only visible when sidebar is closed */}
            {!open && (
                <button
                    onClick={() => setOpen(true)}
                    data-testid="sidebar-toggle"
                    aria-label="Show sidebar"
                    className="fixed top-4 left-4 z-50 flex items-center justify-center w-10 h-10 bg-[var(--surface)] border border-[var(--border-bright)] text-[var(--text)] hover:text-[var(--gold)] hover:border-[var(--gold)] transition-all"
                >
                    <Menu size={16} />
                </button>
            )}

            {/* Mobile backdrop */}
            {open && (
                <div
                    onClick={() => setOpen(false)}
                    className="fixed inset-0 bg-black/60 z-30 lg:hidden"
                    data-testid="sidebar-backdrop"
                />
            )}

            {/* Sidebar */}
            <aside
                className={`fixed top-0 left-0 h-screen w-64 border-r border-[var(--border)] bg-[var(--surface)] flex flex-col z-40 transform transition-transform duration-300 ease-out ${
                    open ? "translate-x-0" : "-translate-x-full"
                }`}
                data-testid="sidebar"
            >
                <div className="p-6 border-b border-[var(--border)] relative">
                    <Logo size="sm" subtitle />
                    <button
                        onClick={() => setOpen(false)}
                        data-testid="sidebar-close"
                        aria-label="Hide sidebar"
                        className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 text-[var(--text-muted)] hover:text-[var(--gold)] border border-transparent hover:border-[var(--gold-deep)]"
                    >
                        <X size={14} />
                    </button>
                </div>
                <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
                    {NAV.map((item) => {
                        if (item.kind === "group") {
                            const activeChild = item.items.some((c) => location.pathname.startsWith(c.to));
                            return (
                                <div key={item.label}>
                                    <button
                                        type="button"
                                        onClick={() => setProgOpen(!progOpen)}
                                        data-testid={item.testid}
                                        className={`w-full flex items-center gap-3 px-3 py-2.5 border-l-2 transition-colors ${
                                            activeChild
                                                ? "border-[var(--gold)] bg-[var(--surface-hover)] text-[var(--text)]"
                                                : "border-transparent text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--surface-hover)]"
                                        }`}
                                    >
                                        <item.icon size={18} />
                                        <span className="text-[14px] font-medium tracking-[0.12em] uppercase flex-1 text-left">{item.label}</span>
                                        <ChevronDown size={14} className={`transition-transform ${progOpen ? "rotate-180" : ""}`} />
                                    </button>
                                    {progOpen && (
                                        <div className="ml-2 mt-1 mb-2 space-y-0.5" data-testid={`${item.testid}-items`}>
                                            {item.items.map((sub) => (
                                                <NavLink
                                                    key={sub.to}
                                                    to={sub.to}
                                                    data-testid={sub.testid}
                                                    onClick={closeOnMobile}
                                                    className={({ isActive }) =>
                                                        `flex items-center gap-3 pl-7 pr-3 py-2 border-l-2 transition-colors ${
                                                            isActive
                                                                ? "border-[var(--gold)] bg-[var(--surface-hover)] text-[var(--gold-bright)]"
                                                                : "border-transparent text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--surface-hover)]"
                                                        }`}>
                                                    <sub.icon size={14} />
                                                    <span className="text-[12px] font-medium tracking-[0.1em] uppercase">{sub.label}</span>
                                                </NavLink>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        }
                        return (
                            <NavLink
                                key={item.to}
                                to={item.to}
                                end={item.end}
                                data-testid={item.testid}
                                onClick={closeOnMobile}
                                className={({ isActive }) =>
                                    `flex items-center gap-3 px-3 py-2.5 border-l-2 transition-colors ${
                                        isActive
                                            ? "border-[var(--gold)] bg-[var(--surface-hover)] text-[var(--text)]"
                                            : "border-transparent text-[var(--text-muted)] hover:text-[var(--text)] hover:bg-[var(--surface-hover)]"
                                    }`
                                }
                            >
                                <item.icon size={18} />
                                <span className="text-[14px] font-medium tracking-[0.12em] uppercase flex-1">{item.label}</span>
                                {item.badgeKey && badges[item.badgeKey] > 0 && (
                                    <span className="px-1.5 py-0.5 bg-[var(--danger)] text-white text-[10px] font-mono" data-testid={`badge-${item.badgeKey}`}>
                                        {badges[item.badgeKey]}
                                    </span>
                                )}
                            </NavLink>
                        );
                    })}
                </nav>
                <div className="border-t border-[var(--border)] p-3">
                    <div className="px-3 py-2 mb-2">
                        <p className="label-tiny">Signed in</p>
                        <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</p>
                        <p className="text-xs text-[var(--text-muted)] truncate font-mono">{user?.email}</p>
                    </div>
                    <button
                        data-testid="logout-btn"
                        onClick={async () => { await logout(); nav("/login"); }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-[var(--text-muted)] hover:text-[var(--danger)] hover:bg-[var(--surface-hover)] text-sm"
                    >
                        <LogOut size={14} /> Sign out
                    </button>
                </div>
            </aside>

            {/* Main content — shifts right only on lg+ when sidebar is open, so mobile stays full screen */}
            <main
                className={`min-h-screen overflow-x-hidden transition-[padding] duration-300 ease-out ${
                    open ? "lg:pl-64" : "pl-0"
                }`}
                data-testid="main-content"
            >
                <div className={open ? "" : "pt-16"}>{children}</div>
            </main>
        </div>
    );
}
